import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import { CircleExclamation } from '@unified-checkout/design-system/icons/index';
import { PageStatus } from '@unified-checkout/design-system';
import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker';

import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  REACH_PAGE_EVENTS,
  REACH_PAGE_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/reach';
import { mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { fetchCampaignsAnalysis } from '@app/dashboard/services/inner_layout/reach/campaign_analysis';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';
import { BOXES_CAMPAIGNS_ANALYSIS_STATICS } from '@app/dashboard/constants/reach/campaigns';
import DateRanges from '@app/dashboard/components/inner_layout/DateRanges';

import {
  buildCampaignEventPayload,
  checkIfPrevDateRangeEqualToCurrent,
  extractCampaignMetrics,
} from '../../utils/campaign-analysis';

import StaticsBoxes from '../../components/StaticsBoxes';

import styles from './CampaignsAnalysis.module.scss';

const CampaignsAnalysis = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const preDefinedRanges = useMemo(() => mainPreDefinedRanges(t), [t]);
  const defaultPreDefinedRange: IPreDefinedRange | undefined =
    preDefinedRanges?.find((range) => range.default);

  const [appliedRanges, setAppliedRanges] = useState<TAppliedRanges>({
    index: defaultPreDefinedRange?.index,
    name: defaultPreDefinedRange?.name,
    start: defaultPreDefinedRange?.selectionRange[0],
    end: defaultPreDefinedRange?.selectionRange[1],
  });

  const prevDateSelected = useRef<TAppliedRanges>(appliedRanges);
  const isFirstMount = useRef(true);

  const {
    data: { data: campaignsAnalysisData } = {},
    isFetching: isCampaignsAnalysisLoading,
    isError: isCampaignsAnalysisError,
  } = useQuery({
    queryKey: [QUERY_KEYS.REACH_GET_CAMPAIGN_ANALYSIS, appliedRanges],
    queryFn: () =>
      fetchCampaignsAnalysis({
        start: formatToISODate(appliedRanges.start as Date),
        end: formatToISODate(appliedRanges.end as Date),
      }),
    enabled: !!appliedRanges.start && !!appliedRanges.end,
  });

  const staticsBoxes = useMemo(() => {
    return BOXES_CAMPAIGNS_ANALYSIS_STATICS(t, campaignsAnalysisData);
  }, [t, campaignsAnalysisData]);

  const handleRangesChange = (ranges: TAppliedRanges) => {
    setAppliedRanges((prev) => {
      const isSame =
        formatToISODate(prev.start!) === formatToISODate(ranges.start!) &&
        formatToISODate(prev.end!) === formatToISODate(ranges.end!);
        
      return isSame ? prev : ranges;
    });
  };

  useEffect(() => {
    const isPrevDateRangeEqualToCurrent = checkIfPrevDateRangeEqualToCurrent({
      appliedRanges,
      prevDateSelected,
    });

    if (isPrevDateRangeEqualToCurrent || isCampaignsAnalysisLoading) return;

    const dateFiltered = `${formatToISODate(
      appliedRanges.start as Date,
      'dd MMM yyyy, hh:mm a'
    )} - ${formatToISODate(appliedRanges.end as Date, 'dd MMM yyyy, hh:mm a')}`;

    const eventKey =
      REACH_PAGE_EVENTS.CAMPAIGNS_ANALYSIS_PAGE
        .CAMPAIGN_ANALYSIS_FILTER_APPLIED;
    const propKey = REACH_PAGE_EVENTS_PROPERTIES.CAMPAIGNS_ANALYSIS_PAGE;
    const metrics = extractCampaignMetrics(campaignsAnalysisData);

    const payload = {
      ...buildCampaignEventPayload(metrics, propKey),
      [propKey.DATE_FILTERED]: dateFiltered,
    };

    prevDateSelected.current = appliedRanges;
    amplitude.track(eventKey, payload);
  }, [appliedRanges, campaignsAnalysisData, isCampaignsAnalysisLoading]);

  useEffect(() => {
    const hasData = !!Object.keys(campaignsAnalysisData || {}).length;
    if (!hasData || !isFirstMount.current) return;

    const propKey = REACH_PAGE_EVENTS_PROPERTIES.CAMPAIGNS_ANALYSIS_PAGE;
    const eventKey =
      REACH_PAGE_EVENTS.CAMPAIGNS_ANALYSIS_PAGE.VIEW_CAMPAIGNS_ANALYSIS_PAGE;
    const metrics = extractCampaignMetrics(campaignsAnalysisData);
    const payload = buildCampaignEventPayload(metrics, propKey);

    isFirstMount.current = false;
    amplitude.track(eventKey, payload);
  }, [campaignsAnalysisData]);

  return isCampaignsAnalysisError ? (
    <PageStatus
      status="error"
      title={t('innerLayout.errors.smthWentWrong')}
      description={t('innerLayout.errors.dataRetrievalErr')}
      icon={CircleExclamation}
    />
  ) : (
    <div className={styles['campaigns-analysis']}>
      <header className={styles['campaigns-analysis__header']}>
        <h1 className={styles['campaigns-analysis__header-heading']}>
          {t('innerLayout.reach.campaignsAnalysis.header.heading')}
        </h1>
        <section className={styles['campaigns-analysis__header-content']}>
          <h2 className={styles['campaigns-analysis__header-content-title']}>
            {t('innerLayout.reach.campaignsAnalysis.header.title')}
          </h2>
          <p className={styles['campaigns-analysis__header-content-subtitle']}>
            {t('innerLayout.reach.campaignsAnalysis.header.subTitle')}
          </p>
        </section>
      </header>
      <DateRanges
        appliedRanges={appliedRanges}
        handleRangesChange={handleRangesChange}
        lang={lang}
        disabled={isCampaignsAnalysisLoading}
        datePickerLabel={t(
          'innerLayout.reach.campaignsAnalysis.header.dateFilterLabel'
        )}
      />
      <StaticsBoxes
        isLoading={isCampaignsAnalysisLoading}
        campaignStaticsList={staticsBoxes}
      />
    </div>
  );
};

export default CampaignsAnalysis;
